import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { environment } from '../../environments/environment';
import { FirebaseErrorHandlerService } from './firebase-error-handler.service';
import { isRestrictedInAppBrowser } from '../in-app-browser';

interface MessagingApi {
  getMessaging: any;
  getToken: any;
  onMessage: any;
  isSupported: () => Promise<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  private platformId = inject(PLATFORM_ID);
  private messaging: any = null;
  private app: any = null;
  private messagingApi: MessagingApi | null = null;
  private supportCache: boolean | null = null;

  constructor(private errorHandler: FirebaseErrorHandlerService) {}

  private async showForegroundNotification(title: string, body: string, link?: string): Promise<void> {
    const notificationOptions = {
      body,
      icon: `${environment.assetBaseUrl}images/logo-colorido.png`,
      data: { link },
    } as NotificationOptions & { image?: string };

    try {
      const notification = new Notification(title, notificationOptions);
      notification.onclick = () => {
        if (link) {
          window.open(link, '_blank');
        }
        notification.close();
      };
      return;
    } catch (error) {
      // Em alguns navegadores mobile o construtor Notification lança "Illegal constructor".
      console.warn('⚠️ Notification constructor indisponível, tentando fallback do Service Worker:', error);
    }

    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.showNotification(title, notificationOptions);
      }
    } catch (error) {
      console.warn('⚠️ Falha ao exibir notificação via Service Worker:', error);
    }
  }

  /**
   * Detecta se é Safari
   */
  private isSafari(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  }

  /**
   * Timeout wrapper para promises
   */
  private async withTimeout<T>(promise: Promise<T>, ms = 10000): Promise<T> {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), ms)
      )
    ]);
  }

  /**
   * Carrega a API do Firebase Messaging dinamicamente
   */
  private async loadMessagingApi(): Promise<MessagingApi | null> {
    if (this.messagingApi) return this.messagingApi;

    if (!isPlatformBrowser(this.platformId)) return null;

    try {
      // Adiciona timeout para o carregamento dinâmico
      const mod = await this.withTimeout(import('firebase/messaging'), 8000);
      this.messagingApi = {
        getMessaging: mod.getMessaging,
        getToken: mod.getToken,
        onMessage: mod.onMessage,
        isSupported: mod.isSupported,
      };
      return this.messagingApi;
    } catch (error) {
      console.warn('Erro ao carregar Firebase Messaging API:', error);
      this.errorHandler.handleMessagingError(error);
      return null;
    }
  }

  /**
   * Verifica se o navegador suporta Firebase Messaging
   */
  async isMessagingSupported(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId) || isRestrictedInAppBrowser()) {
      return false;
    }

    // Cache de suporte para evitar verificações repetidas
    if (this.supportCache !== null) {
      return this.supportCache;
    }

    try {
      // Curto-circuito para Safari
      if (this.isSafari()) {
        console.warn('Firebase Messaging não é suportado no Safari');
        this.supportCache = false;
        return false;
      }

      // Verifica se as APIs básicas estão disponíveis
      if (typeof window === 'undefined' ||
          typeof navigator === 'undefined' ||
          typeof Notification === 'undefined') {
        console.warn('APIs básicas não disponíveis');
        this.supportCache = false;
        return false;
      }

      // Verifica se o Service Worker é suportado
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker não é suportado neste navegador');
        this.supportCache = false;
        return false;
      }

      // Verifica se o IndexedDB é suportado
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB não é suportado neste navegador');
        this.supportCache = false;
        return false;
      }

      // Carrega a API dinamicamente
      const api = await this.loadMessagingApi();
      if (!api) {
        console.warn('Não foi possível carregar a API do Firebase Messaging');
        this.supportCache = false;
        return false;
      }

      // Verifica se o Firebase Messaging é suportado com timeout
      const isFirebaseSupported = await this.withTimeout(api.isSupported().catch(() => false), 5000);
      if (!isFirebaseSupported) {
        console.warn('Firebase Messaging não é suportado neste navegador');
        this.supportCache = false;
        return false;
      }

      this.supportCache = true;
      return true;
    } catch (error) {
      // Usa o tratamento de erros específico
      this.errorHandler.handleMessagingError(error);
      this.supportCache = false;
      return false;
    }
  }

  /**
   * Inicializa o Firebase Messaging
   */
  async initializeMessaging(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId) || isRestrictedInAppBrowser()) {
      return false;
    }

    try {
      // Verifica se já foi inicializado
      if (this.messaging) {
        return true;
      }

      // Verifica suporte antes de inicializar
      const isSupported = await this.isMessagingSupported();
      if (!isSupported) {
        return false;
      }

      // Inicializa o Firebase App (evita múltiplas inicializações)
      this.app = this.app ?? (getApps().length ? getApp() : initializeApp(environment.firebase));

      // Carrega a API e instancia o messaging
      const api = await this.loadMessagingApi();
      if (!api) {
        return false;
      }

      // Inicializa o Firebase Messaging
      this.messaging = api.getMessaging(this.app);

      console.log('✅ Firebase Messaging inicializado com sucesso');
      return true;
    } catch (error) {
      // Usa o tratamento de erros específico
      this.errorHandler.handleMessagingError(error);
      return false;
    }
  }

  /**
   * Solicita permissão e obtém token FCM
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId) || isRestrictedInAppBrowser()) {
      return null;
    }

    try {
      // Inicializa o messaging se necessário
      const initialized = await this.initializeMessaging();
      if (!initialized) {
        return null;
      }

      // Verifica se a API Notification está disponível
      if (typeof Notification === 'undefined' || !('requestPermission' in Notification)) {
        console.warn('API Notification não é suportada neste navegador');
        return null;
      }

      // Solicita permissão
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Permissão de notificação negada pelo usuário');
        return null;
      }

      // Carrega a API
      const api = await this.loadMessagingApi();
      if (!api) {
        return null;
      }

      // Tenta obter o token com Service Worker primeiro
      let token = await this.getTokenWithServiceWorker(api);

      // Se falhar, tenta sem Service Worker
      if (!token) {
        console.log('🔄 Tentando obter token sem Service Worker...');
        token = await this.getTokenWithoutServiceWorker(api);
      }

      if (token) {
        console.log('✅ Token FCM obtido com sucesso');
        return token;
      } else {
        console.warn('❌ Não foi possível obter o token FCM');
        return null;
      }
    } catch (error) {
      // Usa o tratamento de erros específico
      this.errorHandler.handleMessagingError(error);
      return null;
    }
  }

  /**
   * Obtém token FCM com Service Worker
   */
  private async getTokenWithServiceWorker(api: MessagingApi): Promise<string | null> {
    try {
      // Garante que o Service Worker está registrado e pronto
      const swReg = await this.withTimeout(this.ensureServiceWorkerRegistered(), 8000);

      if (!swReg) {
        console.warn('❌ Não foi possível registrar o Service Worker');
        return null;
      }

      // Obtém o token FCM com Service Worker Registration e timeout
      const token = await this.withTimeout(
        api.getToken(this.messaging, {
          vapidKey: environment.firebase.vapidKey,
          serviceWorkerRegistration: swReg,
        }) as Promise<string>,
        10000
      );

      return token;
    } catch (error) {
      console.warn('⚠️ Erro ao obter token com Service Worker:', error);
      return null;
    }
  }

  /**
   * Obtém token FCM sem Service Worker (fallback)
   */
  private async getTokenWithoutServiceWorker(api: MessagingApi): Promise<string | null> {
    try {
      // Tenta obter o token sem especificar o Service Worker
      const token = await this.withTimeout(
        api.getToken(this.messaging, {
          vapidKey: environment.firebase.vapidKey,
        }) as Promise<string>,
        10000
      );

      return token;
    } catch (error) {
      console.warn('⚠️ Erro ao obter token sem Service Worker:', error);
      return null;
    }
  }

  /**
   * Configura listener para mensagens em primeiro plano
   */
  async setupForegroundMessageHandler(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || isRestrictedInAppBrowser()) {
      return;
    }

    try {
      const initialized = await this.initializeMessaging();
      if (!initialized) {
        return;
      }

      const api = await this.loadMessagingApi();
      if (!api) {
        return;
      }

      api.onMessage(this.messaging, async (payload: any) => {
        console.log('📩 Notificação recebida em primeiro plano:', payload);

        const { title, body } = payload.notification ?? {};
        const link = payload.fcmOptions?.link;

        if (title && body) {
          // Só cria notificação se a permissão foi concedida
          if (Notification.permission === 'granted') {
            await this.showForegroundNotification(title, body, link);
          } else {
            // Fallback: log ou toast (implementar conforme necessário)
            console.log('Notificação recebida mas permissão não concedida:', { title, body });
          }
        }
      });

      console.log('✅ Listener de mensagens em primeiro plano configurado');
    } catch (error) {
      // Usa o tratamento de erros específico
      this.errorHandler.handleMessagingError(error);
    }
  }

  /**
   * Verifica se o token FCM está salvo
   */
  getSavedToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return localStorage.getItem('fcmToken');
    } catch (error) {
      console.warn('Erro ao acessar localStorage:', error);
      return null;
    }
  }

  /**
   * Salva o token FCM
   */
  saveToken(token: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem('fcmToken', token);
      console.log('✅ Token FCM salvo no localStorage');
    } catch (error) {
      console.error('❌ Erro ao salvar token FCM:', error);
    }
  }

  /**
   * Remove o token FCM salvo
   */
  removeToken(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.removeItem('fcmToken');
      console.log('✅ Token FCM removido do localStorage');
    } catch (error) {
      console.error('❌ Erro ao remover token FCM:', error);
    }
  }

  /**
   * Verifica se o token mudou
   */
  hasTokenChanged(newToken: string): boolean {
    const savedToken = this.getSavedToken();
    return savedToken !== newToken;
  }

  /**
   * Limpa o cache do Service Worker para resolver problemas de timeout
   */
  private async clearServiceWorkerCache(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !('serviceWorker' in navigator)) {
      return;
    }

    try {
      // Remove registros existentes primeiro
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }

      // Aguarda um pouco para garantir que os registros foram removidos
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Registra um novo Service Worker para forçar atualização
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        updateViaCache: 'none',
        scope: '/'
      });

      // Força atualização se houver uma nova versão
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      console.log('✅ Service Worker cache limpo');
    } catch (error) {
      console.warn('⚠️ Erro ao limpar cache do Service Worker:', error);
    }
  }

    /**
   * Verifica e registra o Service Worker corretamente
   */
  private async ensureServiceWorkerRegistered(): Promise<ServiceWorkerRegistration | null> {
    if (!isPlatformBrowser(this.platformId) || !('serviceWorker' in navigator)) {
      return null;
    }

    try {
      // Verifica se já existe um Service Worker registrado
      const existingRegistration = await navigator.serviceWorker.getRegistration();

      if (existingRegistration) {
        // Se existe, verifica se está ativo
        if (existingRegistration.active) {
          console.log('✅ Service Worker já está ativo');
          return existingRegistration;
        }
      }

      // Registra um novo Service Worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        updateViaCache: 'none',
        scope: '/'
      });

      // Aguarda o Service Worker estar ativo com timeout
      if (registration.installing || registration.waiting) {
        await this.withTimeout(
          new Promise<void>((resolve) => {
            const worker = registration.installing || registration.waiting;
            if (worker) {
              const handleStateChange = () => {
                if (worker.state === 'activated') {
                  worker.removeEventListener('statechange', handleStateChange);
                  resolve();
                }
              };
              worker.addEventListener('statechange', handleStateChange);

              // Se já está ativo, resolve imediatamente
              if (worker.state === 'activated') {
                resolve();
              }
            } else {
              resolve();
            }
          }),
          10000 // 10 segundos de timeout
        );
      }

      console.log('✅ Service Worker registrado e ativo');
      return registration;
    } catch (error) {
      console.error('❌ Erro ao registrar Service Worker:', error);
      return null;
    }
  }

  /**
   * Reinicializa o Firebase Messaging com limpeza de cache
   */
  async reinitializeMessaging(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId) || isRestrictedInAppBrowser()) {
      return false;
    }

    try {
      // Limpa o cache do Service Worker
      await this.clearServiceWorkerCache();

      // Reseta as variáveis de estado
      this.messaging = null;
      this.messagingApi = null;
      this.supportCache = null;

      // Aguarda um pouco antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Tenta inicializar novamente
      return await this.initializeMessaging();
    } catch (error) {
      console.error('❌ Erro ao reinicializar Firebase Messaging:', error);
      this.errorHandler.handleMessagingError(error);
      return false;
    }
  }

  /**
   * Método público para limpar cache do Service Worker
   * Pode ser chamado pelo usuário para resolver problemas
   */
  async clearCache(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      console.log('🧹 Iniciando limpeza de cache do Firebase Messaging...');

      // Remove token salvo
      this.removeToken();

      // Limpa cache do Service Worker
      await this.clearServiceWorkerCache();

      // Reseta variáveis de estado
      this.messaging = null;
      this.messagingApi = null;
      this.supportCache = null;

      console.log('✅ Cache limpo com sucesso. Recarregue a página para reativar as notificações.');
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error);
      throw error;
    }
  }

  /**
   * Verifica o status do Firebase Messaging
   */
  async checkStatus(): Promise<{
    isSupported: boolean;
    isInitialized: boolean;
    hasPermission: boolean;
    hasToken: boolean;
    serviceWorkerStatus: string;
  }> {
    if (!isPlatformBrowser(this.platformId)) {
      return {
        isSupported: false,
        isInitialized: false,
        hasPermission: false,
        hasToken: false,
        serviceWorkerStatus: 'not-available'
      };
    }

    try {
      const isSupported = await this.isMessagingSupported();
      const isInitialized = !!this.messaging;
      const hasPermission = Notification.permission === 'granted';
      const hasToken = !!this.getSavedToken();

      let serviceWorkerStatus = 'unknown';
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        serviceWorkerStatus = registration?.active ? 'active' : 'inactive';
      } else {
        serviceWorkerStatus = 'not-supported';
      }

      return {
        isSupported,
        isInitialized,
        hasPermission,
        hasToken,
        serviceWorkerStatus
      };
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      return {
        isSupported: false,
        isInitialized: false,
        hasPermission: false,
        hasToken: false,
        serviceWorkerStatus: 'error'
      };
    }
  }
}
